<template>
  <PageShell
    title="Reports"
    subtitle="Sales Analysis • category / item performance • profit & last sale"
  >
    <template #actions>
      <button class="ll-pill ghost" type="button" @click="resetFilters">Reset</button>
      <button class="ll-pill" type="button" @click="fetchReport" :disabled="loading">
        {{ loading ? "Generating…" : "Generate" }}
      </button>
    </template>

    <div class="toast" v-if="toast">{{ toast }}</div>

    <section class="card">
      <div class="card-top">
        <div>
          <div class="card-title">Filters</div>
          <div class="micro subtle">
            Choose a range + grouping. “Only Losses” shows negative gross profit rows.
          </div>
        </div>

        <div class="micro dim">
          Rows: <b>{{ fmtInt(filteredRows.length) }}</b>
        </div>
      </div>

      <div class="filters">
        <label class="field">
          <span class="k">Group By</span>
          <select v-model="filterBy" class="input" @change="onGroupChange">
            <option value="category">Category</option>
            <option value="item_no">Item</option>
          </select>
        </label>

        <label class="field" v-if="filterBy === 'category'">
          <span class="k">Category</span>
          <select v-model="filterValue" class="input">
            <option value="">All Categories</option>
            <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
          </select>
        </label>

        <label class="field" v-else>
          <span class="k">Item No</span>
          <input
            type="text"
            v-model="filterValue"
            placeholder="Enter item number"
            class="input"
          />
        </label>

        <label class="field">
          <span class="k">Start</span>
          <input type="date" v-model="startDate" class="input" />
        </label>

        <label class="field">
          <span class="k">End</span>
          <input type="date" v-model="endDate" class="input" />
        </label>

        <label class="toggle danger">
          <input type="checkbox" v-model="onlyLosses" />
          Only Losses
        </label>
      </div>

      <div class="range-row">
        <button
          v-for="r in ranges"
          :key="r.key"
          type="button"
          class="range-chip"
          :class="rangeKey === r.key ? 'on' : ''"
          @click="applyRange(r.key)"
          :title="r.hint"
        >
          {{ r.label }}
        </button>

        <div class="range-spacer"></div>

        <div class="snapshot">
          <div class="snap">
            <div class="k">Revenue</div>
            <div class="v mono">${{ fmtMoney(snapshot.revenue) }}</div>
          </div>
          <div class="snap">
            <div class="k">COGS</div>
            <div class="v mono">${{ fmtMoney(snapshot.cogs) }}</div>
          </div>
          <div class="snap">
            <div class="k">Gross</div>
            <div class="v mono" :class="snapshot.gross < 0 ? 'neg' : ''">
              ${{ fmtMoney(snapshot.gross) }}
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="card empty" v-if="!loading && reportData.length === 0">
      <div class="empty-title">No data yet</div>
      <div class="empty-sub">Pick a range and click Generate.</div>
    </section>

    <section class="card" v-if="loading">
      <div class="skeleton-table">
        <div class="sk-row" v-for="n in 7" :key="n">
          <div class="sk-bar w35"></div>
          <div class="sk-bar w15"></div>
          <div class="sk-bar w15"></div>
          <div class="sk-bar w15"></div>
          <div class="sk-bar w15"></div>
          <div class="sk-bar w15"></div>
          <div class="sk-bar w15"></div>
        </div>
      </div>
    </section>

    <section class="card" v-if="!loading && filteredRows.length">
      <div class="table-wrap">
        <table class="table">
          <thead>
            <tr>
              <th class="left">
                {{ filterBy === "category" ? "Category" : "Item No" }}
              </th>
              <th class="left">Total Sales Qty</th>
              <th class="right">Revenue</th>
              <th class="right">COGS</th>
              <th class="right">Gross</th>
              <th class="right">GP%</th>
              <th class="right">Last Sale</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="row in sortedRows" :key="rowKey(row)">
              <td class="left">
                <div class="row-title">
                  <span class="strong">
                    {{ filterBy === "category" ? row.category_name : row.item_no }}
                  </span>
                  <button
                    class="mini ghost"
                    type="button"
                    title="Copy"
                    @click="copyText(filterBy === 'category' ? row.category_name : row.item_no)"
                  >
                    Copy
                  </button>

                  <span class="heat" :class="gpHeat(row).cls" :title="gpHeat(row).title">
                    {{ gpHeat(row).label }}
                  </span>
                </div>
              </td>

              <td class="left mono">{{ fmtInt(row.total_sales_qty) }}</td>
              <td class="right mono">${{ fmtMoney(row.total_sales_revenue) }}</td>
              <td class="right mono">${{ fmtMoney(row.total_cogs) }}</td>
              <td class="right mono" :class="Number(row.gross_profit || 0) < 0 ? 'neg' : ''">
                ${{ fmtMoney(row.gross_profit) }}
              </td>
              <td class="right mono">
                {{ fmtMoney(row.gross_profit_percent) }}%
              </td>
              <td class="right mono">{{ fmtDate(row.last_sale_date) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </PageShell>
</template>

<script>
import api from "@/services/api";
import PageShell from "@/components/layout/PageShell.vue";

export default {
  name: "SalesAnalysisReport",
  components: { PageShell },

  data() {
    return {
      loading: false,
      toast: "",

      filterBy: "category",
      filterValue: "",
      startDate: "",
      endDate: "",
      rangeKey: "30d",
      onlyLosses: false,

      reportData: [],
      categories: [],

      ranges: [
        { key: "7d", label: "7D", hint: "Last 7 days" },
        { key: "30d", label: "30D", hint: "Last 30 days" },
        { key: "90d", label: "90D", hint: "Last 90 days" },
        { key: "ytd", label: "YTD", hint: "Year to date" },
        { key: "all", label: "ALL", hint: "All time" },
      ],
    };
  },

  computed: {
    filteredRows() {
      const rows = Array.isArray(this.reportData) ? this.reportData : [];
      if (!this.onlyLosses) return rows;
      return rows.filter((r) => Number(r?.gross_profit || 0) < 0);
    },

    sortedRows() {
      // sort by gross profit asc if Only Losses, else revenue desc
      const rows = [...this.filteredRows];
      if (this.onlyLosses) {
        return rows.sort((a, b) => Number(a?.gross_profit || 0) - Number(b?.gross_profit || 0));
      }
      return rows.sort((a, b) => Number(b?.total_sales_revenue || 0) - Number(a?.total_sales_revenue || 0));
    },

    snapshot() {
      const rows = this.filteredRows;
      const revenue = rows.reduce((s, r) => s + Number(r?.total_sales_revenue || 0), 0);
      const cogs = rows.reduce((s, r) => s + Number(r?.total_cogs || 0), 0);
      const gross = rows.reduce((s, r) => s + Number(r?.gross_profit || 0), 0);
      return { revenue, cogs, gross };
    },
  },

  async mounted() {
    this.applyRange("30d");
    await this.fetchCategories();
  },

  methods: {
    rowKey(r) {
      return this.filterBy === "category" ? `c:${r.category_name}` : `i:${r.item_no}`;
    },

    onGroupChange() {
      this.filterValue = "";
    },

    applyRange(key) {
      this.rangeKey = key;

      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const toISODate = (d) => d.toISOString().slice(0, 10);

      if (key === "all") {
        this.startDate = "";
        this.endDate = "";
        return;
      }

      if (key === "ytd") {
        const start = new Date(today.getFullYear(), 0, 1);
        this.startDate = toISODate(start);
        this.endDate = toISODate(today);
        return;
      }

      const days = key === "7d" ? 7 : key === "30d" ? 30 : 90;
      const start = new Date(today);
      start.setDate(start.getDate() - (days - 1));

      this.startDate = toISODate(start);
      this.endDate = toISODate(today);
    },

    resetFilters() {
      this.filterBy = "category";
      this.filterValue = "";
      this.onlyLosses = false;
      this.applyRange("30d");
      this.reportData = [];
    },

    async fetchCategories() {
      try {
        const res = await api.get("/inventory");
        const unique = [...new Set((res.data || []).map((x) => x.category_name).filter(Boolean))];
        this.categories = unique.sort((a, b) => String(a).localeCompare(String(b)));
      } catch (e) {
        console.error("Error fetching categories:", e);
      }
    },

    async fetchReport() {
      try {
        this.loading = true;

        const params = {
          filter_by: this.filterBy,
          filter_value: this.filterValue || "",
          start_date: this.startDate || "",
          end_date: this.endDate || "",
        };

        const res = await api.get("/reports/sales-analysis", { params });
        this.reportData = Array.isArray(res.data) ? res.data : [];
      } catch (e) {
        console.error("Error fetching Sales Analysis report:", e);
        this.reportData = [];
      } finally {
        this.loading = false;
      }
    },

    gpHeat(r) {
      const gp = Number(r?.gross_profit || 0);
      const rev = Number(r?.total_sales_revenue || 0);
      const pct = rev > 0 ? gp / rev : 0;

      if (rev <= 0) return { cls: "neutral", label: "—", title: "No revenue" };
      if (gp < 0) return { cls: "loss", label: "💀 Loss", title: `Negative gross profit (${Math.round(pct * 100)}%)` };
      if (pct >= 0.4) return { cls: "hot", label: "🔥 Hot", title: `Very strong GP% (${Math.round(pct * 100)}%)` };
      if (pct >= 0.25) return { cls: "good", label: "👍 Good", title: `Strong GP% (${Math.round(pct * 100)}%)` };
      if (pct >= 0.1) return { cls: "thin", label: "😬 Thin", title: `Thin GP% (${Math.round(pct * 100)}%)` };
      return { cls: "meh", label: "🫤 Meh", title: `Low GP% (${Math.round(pct * 100)}%)` };
    },

    async copyText(text) {
      const val = String(text || "");
      if (!val) return;
      try {
        await navigator.clipboard.writeText(val);
        this.toast = "Copied";
        setTimeout(() => (this.toast = ""), 900);
      } catch {
        // fallback
        const el = document.createElement("textarea");
        el.value = val;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
        this.toast = "Copied";
        setTimeout(() => (this.toast = ""), 900);
      }
    },

    fmtMoney(n) {
      const x = Number(n || 0);
      return new Intl.NumberFormat("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(x);
    },

    fmtInt(n) {
      const x = Number(n || 0);
      return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(x);
    },

    fmtDate(v) {
      if (!v) return "—";
      try {
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return "—";
        return d.toISOString().slice(0, 10);
      } catch {
        return "—";
      }
    },
  },
};
</script>

<style scoped>
/* Shared platform vibe */
.card {
  border-radius: 16px;
  border: 1px solid rgba(131, 167, 203, 0.85);
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0px 6px 16px rgba(0, 0, 0, 0.06);
  padding: 14px;
  margin-bottom: 14px;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}
.card-title {
  font-weight: 900;
  font-size: 16px;
}

.micro {
  font-size: 12px;
  font-weight: 700;
}
.subtle { opacity: 0.8; }
.dim { opacity: 0.7; }

.filters {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 160px 160px auto;
  gap: 10px;
  align-items: end;
}

.field .k {
  display: block;
  font-size: 12px;
  font-weight: 900;
  opacity: 0.7;
  margin-bottom: 6px;
}

.input {
  width: 100%;
  padding: 12px 12px;
  border-radius: 14px;
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: #f6fbff;
  font-weight: 800;
  font-size: 14px;
}

.toggle {
  display: inline-flex;
  gap: 10px;
  align-items: center;
  font-weight: 900;
  background: #f6fbff;
  border: 1px solid rgba(176, 196, 222, 0.95);
  padding: 10px 12px;
  border-radius: 14px;
  white-space: nowrap;
  height: 44px;
}
.toggle.danger {
  border-color: rgba(180, 60, 60, 0.35);
}

.range-row {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 12px;
}
.range-chip {
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: rgba(246, 251, 255, 0.9);
  padding: 8px 10px;
  border-radius: 999px;
  font-weight: 900;
  cursor: pointer;
}
.range-chip.on {
  border-color: rgba(40, 134, 113, 0.55);
  box-shadow: 0 6px 16px rgba(40, 134, 113, 0.12);
}
.range-spacer { flex: 1; }

.snapshot {
  display: grid;
  grid-template-columns: repeat(3, minmax(120px, 1fr));
  gap: 10px;
}
.snap {
  background: #f6fbff;
  border: 1px solid rgba(176, 196, 222, 0.75);
  border-radius: 14px;
  padding: 10px;
}
.snap .k { font-weight: 900; font-size: 12px; opacity: 0.7; }
.snap .v { font-weight: 900; font-size: 14px; margin-top: 4px; }

.table-wrap { overflow: auto; }
.table {
  width: 100%;
  border-collapse: collapse;
  min-width: 920px;
}
.table th, .table td {
  border: 1px solid rgba(131, 167, 203, 0.55);
  padding: 10px 8px;
  background: rgba(255, 255, 255, 0.65);
}
.table thead th {
  position: sticky;
  top: 0;
  background: #eef4fb;
  z-index: 3;
}
.left { text-align: left; }
.right { text-align: right; }

.row-title {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.mini {
  border: 1px solid rgba(79, 103, 115, 0.25);
  background: #4f6773;
  color: white;
  font-weight: 900;
  padding: 8px 10px;
  border-radius: 999px;
  cursor: pointer;
}
.mini:hover { background: #3a4a5e; }
.mini.ghost { background: #4f6773; }

.heat {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 900;
  font-size: 12px;
  border: 1px solid rgba(176, 196, 222, 0.75);
  background: rgba(246, 251, 255, 0.9);
}
.heat.hot  { border-color: rgba(255, 140, 0, 0.45); }
.heat.good { border-color: rgba(40, 134, 113, 0.45); }
.heat.thin { border-color: rgba(79, 103, 115, 0.55); }
.heat.meh  { border-color: rgba(131, 167, 203, 0.75); }
.heat.loss { border-color: rgba(180, 60, 60, 0.45); }
.heat.neutral { opacity: 0.75; }

.mono {
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}
.strong { font-weight: 900; }
.neg { color: #b43c3c; font-weight: 900; }

.empty { text-align: center; }
.empty-title { font-weight: 900; font-size: 18px; }
.empty-sub { margin-top: 6px; opacity: 0.8; font-weight: 800; }

.toast {
  position: fixed;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 9999;
  padding: 10px 12px;
  border-radius: 999px;
  font-weight: 900;
  border: 1px solid rgba(176, 196, 222, 0.95);
  background: rgba(246, 251, 255, 0.92);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.18);
}

.skeleton-table {
  display: grid;
  gap: 10px;
}
.sk-row {
  display: grid;
  grid-template-columns: 1.2fr repeat(6, 0.6fr);
  gap: 10px;
}
.sk-bar {
  height: 12px;
  border-radius: 999px;
  background: rgba(176, 196, 222, 0.55);
  position: relative;
  overflow: hidden;
}
.sk-bar::after {
  content: "";
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.6) 50%, rgba(255,255,255,0) 100%);
  animation: shimmer 1.2s infinite;
}
@keyframes shimmer { 100% { transform: translateX(100%); } }
.w35 { width: 35%; } .w15 { width: 15%; }

@media (max-width: 980px) {
  .filters { grid-template-columns: 1fr; }
  .snapshot { grid-template-columns: 1fr; width: 100%; }
}
</style>
